import React, { useState, useEffect } from 'react';
import { Download, Search, Eye, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Activities = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [previewActivity, setPreviewActivity] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      // Fetch active books from backend
      const response = await axios.get(`${API_URL}/books`);
      
      // Convert books to activities format
      const bookActivities = response.data
        .filter(book => book.isActive && book.activitySheetsUrl) // Only books with activity sheets
        .map(book => ({
          id: book.id,
          title: `${book.title} - Activity Sheets`,
          description: book.activitySheetsDescription || `Fun worksheets and activities to complement ${book.title}!`,
          category: book.category,
          ageRange: book.ageRange,
          downloadUrl: book.activitySheetsUrl,
          thumbnail: book.coverImage,
          bookTitle: book.title
        }));
      
      setActivities(bookActivities);
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };
  
  const filteredActivities = activities.filter(activity =>
    activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePreview = (activity) => {
    setPreviewActivity(activity);
    setIsPreviewOpen(true);
  };

  const handleDownload = (activity) => {
    toast.success(`Downloading ${activity.title}...`);
    // Mock download - in production this would download actual PDFs
    setIsPreviewOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Free Learning Activities
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Download fun worksheets, puzzles, and activities to complement your Learning Smart books!
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 py-6 text-lg"
            />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading activities...</p>
          </div>
        )}

        {/* No Activities */}
        {!loading && filteredActivities.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              {searchTerm ? 'No activities found matching your search.' : 'No activity sheets available yet.'}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Activity sheets are added when books include downloadable content.
            </p>
          </div>
        )}

        {/* Activities Grid */}
        {!loading && filteredActivities.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredActivities.map((activity) => (
            <Card key={activity.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 group">
              <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                <img
                  src={activity.thumbnail}
                  alt={activity.title}
                  className="w-full h-64 object-contain group-hover:scale-105 transition-transform duration-300 p-4"
                />
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-800">
                  {activity.ageRange}
                </div>
              </div>
              <CardContent className="p-6">
                <div className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  {activity.category}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{activity.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{activity.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Based on {activity.bookTitle}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handlePreview(activity)}
                      size="sm"
                      variant="outline"
                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                    >
                      <Eye className="mr-1 h-4 w-4" />
                      Preview
                    </Button>
                    <Button
                      onClick={() => handleDownload(activity)}
                      size="sm"
                      className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                    >
                      <Download className="mr-1 h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        )}

        {/* Newsletter CTA */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Want More Free Activities?
          </h2>
          <p className="text-xl text-blue-100 mb-6">
            Subscribe to our newsletter for weekly activities delivered to your inbox!
          </p>
          <div className="max-w-md mx-auto flex gap-2">
            <Input
              type="email"
              placeholder="Enter your email"
              className="bg-white text-gray-800"
            />
            <Button variant="secondary" size="lg">
              Subscribe
            </Button>
          </div>
        </div>

        {/* Preview Modal */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {previewActivity && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold flex items-center justify-between">
                    <span>{previewActivity.title}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsPreviewOpen(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6">
                  {/* Activity Details */}
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-block bg-green-100 text-green-800 text-sm font-semibold px-4 py-2 rounded-full">
                      {previewActivity.category}
                    </span>
                    <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-4 py-2 rounded-full">
                      {previewActivity.ageRange}
                    </span>
                    <span className="inline-block bg-purple-100 text-purple-800 text-sm font-semibold px-4 py-2 rounded-full">
                      {previewActivity.downloads.toLocaleString()} downloads
                    </span>
                  </div>

                  {/* Preview Image */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-8 flex items-center justify-center">
                    <img
                      src={previewActivity.thumbnail}
                      alt={previewActivity.title}
                      className="max-w-full max-h-[500px] object-contain rounded-lg shadow-2xl"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">About this Activity</h3>
                    <p className="text-gray-600 leading-relaxed">{previewActivity.description}</p>
                  </div>

                  {/* Additional Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📋 What's Included:</h4>
                    <ul className="list-disc list-inside text-blue-800 space-y-1">
                      <li>Printable PDF worksheet</li>
                      <li>Step-by-step instructions</li>
                      <li>Answer key (where applicable)</li>
                      <li>Learning objectives and tips</li>
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={() => handleDownload(previewActivity)}
                      className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                      size="lg"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Activity
                    </Button>
                    <Button
                      onClick={() => setIsPreviewOpen(false)}
                      variant="outline"
                      size="lg"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Activities;